import { ComponentType, useId } from 'react';
import { format } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';

import ModalActionsNormal from 'components/task-item/modal-actions';
import { TaskItemProps, useTaskItem } from 'components/task-item/useTaskItem';
import ModalActionsMad from 'components/task-item-mad/modal-actions';
import { TaskItemProps as TaskItemMadProps, useTaskItemMad } from 'components/task-item-mad/useTaskItemMad';
import Humanograma from 'components/Humanograma';
import { PersonAvatar, initials } from 'components/person-avatar';
import { FOCO_VISIVEL } from 'utils/foco';

import { AcaoDaFila, acoesGeraisDaFila, CaixaDaFila, caixaDaFila, ModeloDoCartao } from '../acoes-da-fila';
import { rostoDoAutor } from '../identidade-do-participante';
import { etapasDaAgenda, etapasDaAgendaDaFila, PilulaDaEtapa } from './AgendaDaRealizacao';
import AcoesDoParticipante from './AcoesDoParticipante';
import { autorDaRealizacao, humanogramaDoAutor } from './CabecalhoDaRealizacao';
import { periodoDaEntregaNaFila } from './CamposDaRealizacao';
import DetalhesDaRealizacao from './DetalhesDaRealizacao';
import TrilhaDaRealizacao from './TrilhaDaRealizacao';

function dataCurta(ms: number): string {
  return format(new Date(ms), 'd MMM. yy', { locale: ptBR });
}

function necDa(data: any): string {
  if (data.nec?.cd_alcc) return `${data.nec.cd_alcc} - ${data.nec.nm_alcc}`;
  if (typeof data.nec === 'string' && data.nec.length) return data.nec;
  return '---';
}

const ACAO_DE_TEXTO = `cursor-pointer border-0 bg-transparent p-0 text-[10.5px] font-bold uppercase tracking-[0.04em] hover:underline ${FOCO_VISIVEL}`;
const BOTAO_DA_ACAO = `inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-jj-sm border px-5 py-[11px] text-[11px] font-bold uppercase tracking-[0.04em] ${FOCO_VISIVEL}`;
// SPEC_DEVIATION: a ação principal usa `brand-blue-dark` no lugar do `brand-blue` do design (amarelo de 11px no azul
// fica abaixo de 4,5:1), a mesma regra do Feed e da Minha Equipe.
const BOTAO_PRINCIPAL = 'border-brand-blue-dark bg-brand-blue-dark text-brand-yellow';
const BOTAO_SECUNDARIO = 'border-brand-blue-dark bg-brand-white text-brand-blue-dark';
const BOTAO_CONTORNADO = `inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-jj-sm border border-surface-300 bg-brand-white px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.04em] text-brand-blue ${FOCO_VISIVEL}`;

function textoDoAvisoDePendencia(modelo: ModeloDoCartao) {
  if (modelo.isParticipantsApprover) {
    return (
      <>
        A quantidade de participantes ainda <strong>não foi aprovada</strong>, e você tem alçada para autorizá-la. Ao
        apreciar esta realização, a quantidade será <strong>aprovada automaticamente</strong> e ela passará a ser
        pontuada.
      </>
    );
  }
  const gestor = modelo.data?.managers_to_approve?.[0]?.nome_guerra;
  return (
    <>
      A quantidade de participantes ainda <strong>não foi aprovada</strong>. Você pode apreciar normalmente, mas a
      realização só será pontuada após a aprovação da quantidade de participantes
      {gestor && (
        <>
          {' '}
          pelo gestor <strong>{gestor}</strong>
        </>
      )}
      .
    </>
  );
}

/**
 * RDV-29: "Aguardando apreciação" vira "Aguardando sua apreciação" só quando a caixa do cartão oferece ao gestor uma
 * ação de apreciação; sem ela (etapa de outro gestor, realização do par no MAD), o status fica como está.
 */
function pilulaDaFila(status: string | undefined, caixa: CaixaDaFila): string | undefined {
  const apreciacaoEhSua = caixa.tipo === 'apreciacao' && caixa.acoes.length > 0;
  return status === 'Aguardando apreciação' && apreciacaoEhSua ? 'Aguardando sua apreciação' : status;
}

interface CorpoDoCartaoProps {
  modelo: ModeloDoCartao;
  ModalActions: ComponentType<any>;
}

/** Cartão da fila do gestor no visual do design (RDN-43, RDN-50), sobre o modelo do `TaskItem`/`TaskItemMad`. */
function CorpoDoCartaoDaFila({ modelo, ModalActions }: CorpoDoCartaoProps) {
  const { data, playerInfo } = modelo;
  const tituloId = useId();
  const detalhesId = useId();
  const gerais = acoesGeraisDaFila(modelo);
  const caixa = caixaDaFila(modelo);
  const etapaAtual = etapasDaAgenda(modelo).find(etapa => etapa.atual);
  const trilha = etapasDaAgendaDaFila(modelo);
  const participantes = Array.isArray(data.team) ? data.team.length : undefined;
  const gestorQueAutoriza = data.managers_to_approve?.[0];
  const detalhesCarregados = !!data.team && !!data.target;
  const estados = [...caixa.estados, ...(gerais.feedback === 'enviado' ? ['Feedback enviado'] : [])];

  function alternarDetalhes() {
    const vaiAbrir = !modelo.oppened;
    if (vaiAbrir && (!data.team || !data.target)) modelo.handleReload();
    modelo.setOppened(vaiAbrir);
  }

  function botaoDaAcao(acao: AcaoDaFila) {
    return (
      <button
        key={acao.id}
        type="button"
        title={acao.dica}
        onClick={acao.executar}
        className={`${BOTAO_DA_ACAO} ${acao.principal ? BOTAO_PRINCIPAL : BOTAO_SECUNDARIO}`}
      >
        {acao.rotulo}
      </button>
    );
  }

  return (
    <article
      aria-labelledby={tituloId}
      className="overflow-hidden rounded-[14px] border border-surface-300 bg-brand-white shadow-[0_1px_3px_rgba(17,24,39,.05)]"
    >
      {gerais.solicitarAutorizacao && (
        <div className="flex flex-wrap items-center gap-3 bg-brand-yellow-light px-5 py-3">
          <p className="m-0 flex-1 text-xs text-brand-blue-dark">
            {`Número de participantes pendentes de autorização pelo gestor ${gestorQueAutoriza.nome_guerra}`}
          </p>
          <button
            type="button"
            title={`Solicitar liberação para ${gestorQueAutoriza.nome_guerra} (${gestorQueAutoriza.chave})`}
            onClick={() =>
              modelo.onOpenModalSendMessage &&
              modelo.onOpenModalSendMessage({
                managerName: gestorQueAutoriza.nome_guerra,
                participantCount: data?.participants_approval?.total,
                taskTitle: data.title,
                managerId: gestorQueAutoriza.chave,
              })
            }
            className={`${BOTAO_DA_ACAO} ${BOTAO_PRINCIPAL}`}
          >
            Solicitar autorização
          </button>
        </div>
      )}

      {gerais.avisoDeParticipantesPendentes && (
        <div className="bg-brand-yellow-light px-5 py-3">
          <p className="m-0 text-xs leading-[1.6] text-brand-blue-dark">{textoDoAvisoDePendencia(modelo)}</p>
        </div>
      )}

      <div className="px-5 pb-[18px] pt-5">
        <div className="flex flex-wrap items-start gap-3.5">
          {/* Avatar montado, foto ou iniciais de quem cadastrou, como o `UserAvatar` do cartão antigo. */}
          <PersonAvatar
            {...rostoDoAutor(data)}
            initials={initials(data.owner_name_guerra ?? '')}
            size={42}
            initialsClassName="bg-brand-green font-display text-[13px] font-bold text-brand-navy"
          />
          <div className="min-w-0 flex-[1_1_300px]">
            <h3 id={tituloId} className="m-0 font-display text-[17px] font-bold leading-[1.3] text-brand-blue-dark">
              {data.title || '---'}
            </h3>
            <p className="m-0 mt-[7px] text-[11.5px] leading-[1.5] text-content-soft">
              {/* Dica do cadastrante com os mesmos campos que o `TaskItem` antigo passava (RDV-25). */}
              <Humanograma data={humanogramaDoAutor(autorDaRealizacao(data))}>
                <span className="font-bold text-link hover:text-link-hover">
                  {`${data.owner_name_guerra?.split(' ')[0] ?? ''} (${data.owner})`}
                </span>
              </Humanograma>
              {` · cadastrou em ${dataCurta(data.created)}`}
              {!!data.updated && ` · atualizada em ${dataCurta(data.updated)}`}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-1.5 md:flex-col md:items-end">
            <PilulaDaEtapa status={pilulaDaFila(etapaAtual?.status, caixa)} />
            <span
              className={`whitespace-nowrap rounded-jj-pill border px-[11px] py-1 text-[9.5px] font-bold uppercase tracking-[0.04em] ${
                modelo.hasFeedback ? 'border-brand-green text-brand-navy' : 'border-surface-300 text-content'
              }`}
            >
              {modelo.hasFeedback ? 'Feedback concedido' : 'Sem feedback'}
            </span>
            {gerais.editar && (
              <div className="flex gap-4">
                <button type="button" onClick={modelo.handleEdit} className={`${ACAO_DE_TEXTO} text-brand-blue`}>
                  Editar
                </button>
              </div>
            )}
          </div>
        </div>

        <dl className="m-0 mt-4 grid grid-cols-2 gap-3.5 border-t border-surface-200 pt-3.5 md:grid-cols-4">
          <div className="min-w-0">
            <dt className="mb-[5px] text-[9.5px] uppercase tracking-[0.05em] text-content-soft">Indicador</dt>
            <dd className="m-0 text-xs leading-[1.5] text-content">
              {data.indicator?.nome || '---'}
              {gerais.atualizarIndicador && (
                <button
                  type="button"
                  onClick={modelo.onUpdateIndicator}
                  className={`mt-[7px] flex items-center gap-[5px] rounded-jj-sm border border-surface-300 bg-brand-white px-[9px] py-1 text-[9.5px] font-bold uppercase tracking-[0.04em] text-brand-blue ${FOCO_VISIVEL}`}
                >
                  Atualizar
                </button>
              )}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="mb-[5px] text-[9.5px] uppercase tracking-[0.05em] text-content-soft">NEC/Iniciativa</dt>
            <dd className="m-0 text-xs leading-[1.5] text-content">{necDa(data)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="mb-[5px] text-[9.5px] uppercase tracking-[0.05em] text-content-soft">Período da entrega</dt>
            <dd className="m-0 text-xs leading-[1.5] text-content">
              {periodoDaEntregaNaFila(data.start, data.end)}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="mb-[5px] text-[9.5px] uppercase tracking-[0.05em] text-content-soft">Participantes</dt>
            <dd className="m-0 text-xs leading-[1.5] text-content">
              {typeof participantes === 'number' ? `${participantes} funcionários` : '---'}
            </dd>
          </div>
        </dl>

        {/* RDV-29: a trilha do participante, com o Pit Stop da regra da fila e sem o período de cada etapa. */}
        <div className="mt-3.5">
          <TrilhaDaRealizacao etapas={trilha} />
        </div>

        {/* SPEC_DEVIATION: o prazo do `evaluationData` vem do `Countdown` com o vermelho antigo (#ED2B48, 4,2:1 no
            branco); aqui ele passa ao `text-urgent` da nova identidade sem tocar no hook. */}
        <div className="mt-3 text-[11.5px] text-content-soft empty:hidden [&_.msg-countdown_strong_*]:!text-urgent">
          {modelo.evaluationData}
        </div>

        <div id={detalhesId}>
          {modelo.oppened &&
            (detalhesCarregados ? (
              <DetalhesDaRealizacao
                data={data}
                getUserTeam={modelo.getUserTeam}
                getFormattedDate={modelo.getFormattedDate}
                getStepLabel={modelo.getStepLabel}
                ocultarPercentual={modelo.ecoa}
                acoesDoParticipante={pessoa => <AcoesDoParticipante pessoa={pessoa} modelo={modelo} />}
              />
            ) : (
              <p role="status" className="m-0 mt-4 text-center text-xs text-content-soft">
                Carregando informações…
              </p>
            ))}
        </div>

        {caixa.tipo === 'participantes-rejeitados' && (
          <p className="m-0 mt-4 rounded-jj-md bg-surface-100 px-4 py-3 text-center text-xs text-content">
            A quantidade de participantes desta realização <strong>não foi autorizada</strong>. Por isso, ela não pode ser
            apreciada.
          </p>
        )}
        {caixa.tipo === 'apreciacao' && (
          <div className="mt-4 space-y-1">
            {modelo.pitStopCanEvaluate && (
              <p className="m-0 text-xs font-bold text-brand-blue-dark">Essa Realização Destaque foi promovida pela sua Rede.</p>
            )}
            {!!modelo.questionTitle && <h4 className="m-0 text-[13px] font-bold text-brand-blue-dark">{modelo.questionTitle}</h4>}
          </div>
        )}
        {caixa.tipo === 'autorizacao' && (
          <h4 className="m-0 mt-4 text-[13px] font-bold text-brand-blue-dark">
            {`Esta realização possui ${data?.team?.length} participantes. Você autoriza esse número de participantes?`}
          </h4>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-t border-surface-200 bg-surface-100 px-5 py-3.5">
        {caixa.acoes.map(botaoDaAcao)}
        {estados.length > 0 && (
          <ul aria-label="Situação da apreciação" className="m-0 flex list-none flex-wrap gap-2.5 p-0">
            {estados.map(estado => (
              <li key={estado} className="text-xs font-bold text-content">
                {estado}
              </li>
            ))}
          </ul>
        )}
        {gerais.desfazer && (
          <button
            type="button"
            onClick={() => modelo.showModal(`validation-reset-${data._id}`)}
            // SPEC_DEVIATION: `brand-blue-dark` no lugar do `brand-blue` do design (4,3:1 no cinza do rodapé).
            className={`${ACAO_DE_TEXTO} text-brand-blue-dark`}
          >
            Desfazer
          </button>
        )}
        {gerais.feedback === 'enviar' && (
          <>
            <span aria-hidden="true" className="h-6 w-px shrink-0 bg-surface-300" />
            <button
              type="button"
              title="Conte à equipe o que achou dessa entrega"
              onClick={() => modelo.showModal(`validation-feedback-${data._id}`)}
              className={BOTAO_CONTORNADO}
            >
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Enviar feedback
            </button>
          </>
        )}
        <button
          type="button"
          aria-expanded={modelo.oppened}
          aria-controls={detalhesId}
          onClick={alternarDetalhes}
          className={`ml-auto inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent px-3.5 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.04em] text-content ${FOCO_VISIVEL}`}
        >
          {`Ver ${modelo.oppened ? 'menos' : 'mais'} informações`}
          <svg
            aria-hidden="true"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${modelo.oppened ? 'rotate-180' : ''}`}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>

      <ModalActions
        callback={modelo.callBackActions}
        id={data._id}
        status={modelo.status}
        data={data}
        playerInfo={playerInfo}
        validateTeamMember={modelo.validateTeamMember}
        activeEvaluationStep={modelo.activeEvaluationStep as number}
        bonus={modelo.bonus}
        userToChange={modelo.userToChange}
        ecoa={modelo.ecoa}
      />
    </article>
  );
}

function CartaoDaFilaNormal(props: TaskItemProps) {
  return <CorpoDoCartaoDaFila modelo={useTaskItem(props)} ModalActions={ModalActionsNormal} />;
}

function CartaoDaFilaMad(props: TaskItemMadProps) {
  return <CorpoDoCartaoDaFila modelo={useTaskItemMad(props) as ModeloDoCartao} ModalActions={ModalActionsMad} />;
}

export type CartaoDaFilaProps =
  | ({ variante: 'normal' } & TaskItemProps)
  | ({ variante: 'mad' } & TaskItemMadProps);

/**
 * Cartão da fila de apreciação (RDN-43, RDN-50). Recebe as mesmas props que a fila passa hoje ao `TaskItem`
 * (variante `normal`) ou ao `TaskItemMad` (variante `mad`) e usa o hook e os `ModalActions` do mesmo pacote.
 */
export default function CartaoDaFila({ variante, ...props }: CartaoDaFilaProps) {
  return variante === 'mad' ? <CartaoDaFilaMad {...(props as TaskItemMadProps)} /> : <CartaoDaFilaNormal {...props} />;
}
